<template>
  <div class="container-fluid mb-4">
    <div class="row justify-content-center">
      <div class="default-page-width">
        <div class="card card-default">
          <div class="card-header">{{$t('serviceplans.title')}}</div>
          <div class="card-body">
            <ul class="nav nav-tabs mb-3">
              <li class="nav-item">
                <a class="nav-link" :class="{active: activeTab === 'plans'}" href="#" @click.prevent="activeTab = 'plans'">{{$t('serviceplans.tabs.plans') || 'Servicepläne'}}</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{active: activeTab === 'groups'}" href="#" @click.prevent="activeTab = 'groups'">{{$t('serviceplans.tabs.groups') || 'Gruppen'}}</a>
              </li>
            </ul>

            <div v-if="activeTab === 'plans'">
              <div class="mb-3 d-flex align-items-center">
                <input class="form-control mr-2" style="max-width: 320px" v-model="search" :placeholder="$t('serviceplans.search')" @keyup.enter="loadPlans"/>
                <button class="btn btn-secondary mr-2" @click="loadPlans">{{$t('general.search') || 'Suchen'}}</button>
                <button class="btn btn-primary" v-if="canManage" @click="startCreate">{{$t('serviceplans.create')}}</button>
              </div>

              <div class="table-responsive">
                <table class="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>{{$t('serviceplans.name')}}</th>
                      <th>{{$t('serviceplans.enabled')}}</th>
                      <th>{{$t('serviceplans.trigger')}}</th>
                      <th>{{$t('serviceplans.prewarn_days')}}</th>
                      <th>{{$t('serviceplans.auto_create_order')}}</th>
                      <th class="text-right" style="width:180px">{{$t('serviceplans.actions')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in plans" :key="p.id">
                      <td>
                        {{p.name}}
                        <div v-if="p.description" class="small text-muted text-truncate" style="max-width: 250px" :title="p.description">
                          {{p.description}}
                        </div>
                      </td>
                      <td>
                        <span class="badge" :class="p.enabled ? 'badge-success' : 'badge-secondary'">{{ p.enabled ? $t('general.enabled') : $t('general.disabled') }}</span>
                      </td>
                      <td>
                        <span v-if="p.trigger_time_unit && p.trigger_time_value">{{p.trigger_time_value}} {{unitLabel(p.trigger_time_unit)}}</span>
                        <span v-else>{{$t('serviceplans.no-time-trigger')}}</span>
                      </td>
                      <td>{{p.prewarn_days || 0}}</td>
                      <td>
                        <span class="badge" :class="p.auto_create_order ? 'badge-info' : 'badge-secondary'">{{ p.auto_create_order ? $t('general.yes') : $t('general.no') }}</span>
                      </td>
                      <td class="text-right">
                        <button class="btn btn-sm btn-outline-primary mr-2" v-if="canManage" @click="startEdit(p)"><i class="far fa-edit"></i></button>
                        <button class="btn btn-sm btn-outline-danger" v-if="canManage" @click="doDelete(p)"><i class="far fa-trash"></i></button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="activeTab === 'groups'">
              <div class="mb-3 d-flex align-items-center">
                <button class="btn btn-primary" v-if="canManage" @click="startCreateGroup">{{$t('serviceplans.groups.create') || 'Neue Gruppe'}}</button>
              </div>

              <div class="table-responsive">
                <table class="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>{{$t('serviceplans.name')}}</th>
                      <th>{{$t('serviceplans.enabled')}}</th>
                      <th>{{$t('serviceplans.groups.included_plans') || 'Enthaltene Pläne'}}</th>
                      <th class="text-right" style="width:180px">{{$t('serviceplans.actions')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="g in groups" :key="g.id">
                      <td>{{g.name}}</td>
                      <td>
                        <span class="badge" :class="g.enabled ? 'badge-success' : 'badge-secondary'">{{ g.enabled ? $t('general.enabled') : $t('general.disabled') }}</span>
                      </td>
                      <td>
                        <span class="badge badge-info mr-1" v-for="p in g.service_plans" :key="p.id">{{p.name}}</span>
                      </td>
                      <td class="text-right">
                        <button class="btn btn-sm btn-outline-primary mr-2" v-if="canManage" @click="startEditGroup(g)"><i class="far fa-edit"></i></button>
                        <button class="btn btn-sm btn-outline-danger" v-if="canManage" @click="doDeleteGroup(g)"><i class="far fa-trash"></i></button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Edit/Create Drawer/Modal -->
            <div v-if="showForm && activeTab === 'plans'">
              <hr/>
              <h5 class="mb-3">{{ isEdit ? $t('serviceplans.edit_title') : $t('serviceplans.create_title') }}</h5>
              <form @submit.prevent="savePlan">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{$t('serviceplans.name')}}</label>
                    <input type="text" class="form-control" v-model="form.name" required />
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.enabled')}}</label>
                    <div><input type="checkbox" v-model="form.enabled" /> {{$t('general.enabled')}}</div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.trigger_time_unit')}}</label>
                    <select class="form-control" v-model="form.trigger_time_unit">
                      <option :value="null">{{$t('general.none')}}</option>
                      <option value="day">{{$t('objects.form.offset-unit.day')}}</option>
                      <option value="week">{{$t('objects.form.offset-unit.week')}}</option>
                      <option value="month">{{$t('objects.form.offset-unit.month')}}</option>
                      <option value="year">{{$t('objects.form.offset-unit.year')}}</option>
                    </select>
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.trigger_time_value')}}</label>
                    <input type="number" min="1" class="form-control" :disabled="!form.trigger_time_unit" v-model.number="form.trigger_time_value" />
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.prewarn_days')}}</label>
                    <input type="number" min="0" class="form-control" v-model.number="form.prewarn_days" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.auto_create_order')}}</label>
                    <div><input type="checkbox" v-model="form.auto_create_order" /> {{$t('general.yes')}}</div>
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.order_due_in_days')}}</label>
                    <input type="number" min="0" class="form-control" :disabled="!form.auto_create_order" v-model.number="form.order_due_in_days" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.notify_pool_manager')}}</label>
                    <div><input type="checkbox" v-model="form.notify_pool_manager" /> {{$t('general.yes')}}</div>
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.notify_repeat_weekly')}}</label>
                    <div><input type="checkbox" v-model="form.notify_repeat_weekly" /> {{$t('general.yes')}}</div>
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.notify_requires_confirmation')}}</label>
                    <div><input type="checkbox" v-model="form.notify_requires_confirmation" /> {{$t('general.yes')}}</div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{$t('serviceplans.notify_channels')}}</label>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="nc_db" value="database" :checked="channelsHas('database')" @change="toggleChannel('database', $event)">
                      <label class="form-check-label" for="nc_db">database</label>
                    </div>
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" id="nc_mail" value="mail" :checked="channelsHas('mail')" @change="toggleChannel('mail', $event)">
                      <label class="form-check-label" for="nc_mail">mail</label>
                    </div>
                    <small class="form-text text-muted">{{$t('serviceplans.notify_channels_hint')}}</small>
                    <div v-if="channelsHas('mail')" class="alert alert-info mt-2 p-2">
                      {{$t('serviceplans.mail_hint')}}
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-4">
                    <label>{{$t('serviceplans.trigger_rent_days')}}</label>
                    <input type="number" min="1" class="form-control" v-model.number="form.trigger_rent_days" />
                  </div>
                  <div class="form-group col-md-4">
                    <label>{{$t('serviceplans.combine_mode')}}</label>
                    <select class="form-control" v-model="form.combine_mode">
                      <option value="earliest">earliest</option>
                      <option value="and">and</option>
                      <option value="or">or</option>
                    </select>
                  </div>
                </div>

                <div class="form-group">
                  <label>{{$t('serviceplans.description')}}</label>
                  <textarea class="form-control" rows="3" v-model="form.description"></textarea>
                </div>
                <div class="form-group">
                  <label>{{$t('serviceplans.webhook_url')}}</label>
                  <input type="url" class="form-control" v-model="form.webhook_url"/>
                </div>

                <div class="mt-3 mb-3">
                  <button type="button" class="btn btn-secondary mr-2" @click="cancelEdit">{{$t('general.cancel')}}</button>
                  <button type="submit" class="btn btn-success">{{$t('general.save')}}</button>
                </div>
              </form>
            </div>

            <!-- Group Edit/Create -->
            <div v-if="showGroupForm && activeTab === 'groups'">
              <hr/>
              <h5 class="mb-3">{{ isEdit ? ($t('serviceplans.groups.edit_title') || 'Gruppe bearbeiten') : ($t('serviceplans.groups.create_title') || 'Gruppe erstellen') }}</h5>
              <form @submit.prevent="saveGroup">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{$t('serviceplans.name')}}</label>
                    <input type="text" class="form-control" v-model="groupForm.name" required />
                  </div>
                  <div class="form-group col-md-3">
                    <label>{{$t('serviceplans.enabled')}}</label>
                    <div><input type="checkbox" v-model="groupForm.enabled" /> {{$t('general.enabled')}}</div>
                  </div>
                </div>

                <div class="form-group">
                  <label>{{$t('serviceplans.groups.included_plans') || 'Enthaltene Pläne'}}</label>
                  <div class="row">
                    <div class="col-md-4" v-for="p in plans" :key="p.id">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" :id="'plan_'+p.id" :value="p.id" v-model="groupForm.service_plan_ids">
                        <label class="form-check-label" :for="'plan_'+p.id">{{p.name}}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>{{$t('serviceplans.description')}}</label>
                  <textarea class="form-control" rows="3" v-model="groupForm.description"></textarea>
                </div>

                <div class="mt-3 mb-3">
                  <button type="button" class="btn btn-secondary mr-2" @click="cancelEditGroup">{{$t('general.cancel')}}</button>
                  <button type="submit" class="btn btn-success">{{$t('general.save')}}</button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'service-plans',
  data() {
    return {
      activeTab: 'plans', // 'plans' or 'groups'
      plans: [],
      groups: [],
      search: '',
      showForm: false,
      isEdit: false,
      form: this.emptyForm(),
      groupForm: this.emptyGroupForm(),
      showGroupForm: false,
    };
  },
  computed: {
    canManage() {
      return AshnetApp.can && AshnetApp.can('serviceplan manage');
    },
  },
  mounted() {
    if (!this.canManage) {
      swal.fire({ text: 'Unauthorized access', icon: 'error', timer: 1000 }).then(()=>{
        this.$router.replace('/');
      });
      return;
    }
    this.loadPlans();
    this.loadGroups();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        enabled: true,
        trigger_time_unit: null,
        trigger_time_value: null,
        trigger_rent_days: null,
        combine_mode: 'earliest',
        notify_pool_manager: true,
        notify_channels: ['database'],
        notify_repeat_weekly: true,
        notify_requires_confirmation: false,
        auto_create_order: false,
        order_due_in_days: 0,
        prewarn_days: 0,
        description: '',
        webhook_url: ''
      };
    },
    emptyGroupForm() {
      return {
        id: null,
        name: '',
        description: '',
        enabled: true,
        service_plan_ids: []
      };
    },
    async loadGroups() {
      try {
        const { data } = await axios.get('/api/service-plan-groups');
        this.groups = data.data || [];
      } catch (e) { console.error(e); }
    },
    startCreateGroup() {
      this.isEdit = false;
      this.groupForm = this.emptyGroupForm();
      this.showGroupForm = true;
    },
    startEditGroup(g) {
      this.isEdit = true;
      this.groupForm = {
        id: g.id,
        name: g.name,
        description: g.description,
        enabled: !!g.enabled,
        service_plan_ids: g.service_plans ? g.service_plans.map(p => p.id) : []
      };
      this.showGroupForm = true;
    },
    cancelEditGroup() {
      this.showGroupForm = false;
    },
    async saveGroup() {
      try {
        if (this.isEdit) {
          await axios.put(`/api/service-plan-groups/${this.groupForm.id}`, this.groupForm);
        } else {
          await axios.post('/api/service-plan-groups', this.groupForm);
        }
        this.showGroupForm = false;
        this.loadGroups();
        swal.fire({ icon: 'success', title: this.$t('general.saved'), timer: 1000 });
      } catch (e) {
        console.error(e);
        swal.fire({ icon: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error saving group' });
      }
    },
    async doDeleteGroup(g) {
      try {
        const ok = await swal.fire({
          title: this.$t('general.are_you_sure'),
          text: this.$t('general.cannot_be_undone'),
          icon: 'warning',
          showCancelButton: true
        });
        if (!ok.value) return;
        await axios.delete(`/api/service-plan-groups/${g.id}`);
        this.loadGroups();
        swal.fire({ icon: 'success', title: this.$t('general.deleted'), timer: 1000 });
      } catch (e) {
        console.error(e);
        swal.fire({ icon: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error deleting group' });
      }
    },
    unitLabel(u) {
      switch(u){
        case 'day': return this.$t('objects.form.offset-unit.day');
        case 'week': return this.$t('objects.form.offset-unit.week');
        case 'month': return this.$t('objects.form.offset-unit.month');
        case 'year': return this.$t('objects.form.offset-unit.year');
        default: return '';
      }
    },
    channelsHas(name){
      return Array.isArray(this.form.notify_channels) && this.form.notify_channels.includes(name);
    },
    toggleChannel(name, ev){
      const checked = ev.target.checked;
      if(!Array.isArray(this.form.notify_channels)) this.form.notify_channels = [];
      if(checked && !this.form.notify_channels.includes(name)) this.form.notify_channels.push(name);
      if(!checked) this.form.notify_channels = this.form.notify_channels.filter(x => x !== name);
      if(this.form.notify_channels.length === 0){
        this.form.notify_channels = ['database'];
      }
    },
    startCreate(){
      this.isEdit = false;
      this.form = this.emptyForm();
      this.showForm = true;
    },
    startEdit(p){
      this.isEdit = true;
      this.form = { ...this.emptyForm(), ...p };
      // Ensure channels default
      if(!this.form.notify_channels || this.form.notify_channels.length === 0){
        this.form.notify_channels = ['database'];
      }
      this.showForm = true;
    },
    cancelEdit(){
      this.showForm = false;
      this.form = this.emptyForm();
    },
    async loadPlans(){
      const params = {};
      if(this.search){ params.search = this.search; }
      const resp = await axios.get('/api/service-plans', { params });
      this.plans = (resp.data && resp.data.data) ? resp.data.data.map(x => {
        return {
          ...x,
          name: x.text || x.name,
        };
      }) : [];
      // For full CRUD details we might need a dedicated show endpoint;
      // for now, edits will submit the currently visible fields only.
    },
    async savePlan(){
      try{
        // Basic validation: if a unit is set, value must be >= 1
        if(this.form.trigger_time_unit && (!this.form.trigger_time_value || this.form.trigger_time_value < 1)){
          await swal.fire({title: this.$t('general.warning'), text: this.$t('serviceplans.validation.time_value_required'), icon: 'warning'});
          return;
        }
        const payload = { ...this.form };
        if(this.isEdit && this.form.id){
          await axios.put(`/api/service-plans/${this.form.id}`, payload);
        } else {
          const { data } = await axios.post('/api/service-plans', payload);
          if (data && data.data) {
            this.form.id = data.data.id;
          }
        }
        await this.loadPlans();
        this.cancelEdit();
        swal.fire({ icon: 'success', title: this.$t('general.saved'), timer: 800 });
      }catch(e){
        console.error(e);
        swal.fire({ icon: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error' });
      }
    },
    async doDelete(p){
      const ok = await swal.fire({ title: this.$t('general.are_you_sure'), text: this.$t('general.cannot_be_undone'), icon: 'warning', showCancelButton: true });
      if(!ok.value) return;
      try{
        await axios.delete(`/api/service-plans/${p.id}`);
        await this.loadPlans();
        swal.fire({ icon: 'success', title: this.$t('general.deleted'), timer: 800 });
      }catch(e){
        console.error(e);
        swal.fire({ icon: 'error', text: (e.response && e.response.data && e.response.data.message) || 'Error' });
      }
    }
  }
}
</script>

<style scoped>
.table td, .table th { vertical-align: middle; }
</style>
